import React from 'react';
import { Menu, MenuItem, ListItemIcon, ListItemText, Typography, Divider } from '@mui/material';
import {
  Message as MessageIcon,
  RadioButtonChecked as ChoiceIcon,
  CheckBox as MultipleChoiceIcon,
  TextFields as TemplateIcon,
  NavigateNext as NavigateIcon,
  Edit as FormFieldsIcon,
  Storage as ContextIcon,
  AutoAwesome as GenerateIcon,
  Timer as DelayIcon,
  Search as SearchIcon,
  AccountTree as SubPresetIcon,
  LocationOn as AddressIcon,
  AutoAwesome,
  BugReport as DebugIcon,
  Image as PicturesIcon,
  Person as UserIcon,
  Business as NonprofitsIcon,
  Timer as TimerIcon,
  Code as CodeIcon,
  AdminPanelSettings as ReduxIcon,
} from '@mui/icons-material';

import { ACTION_TYPES, CALL_METHODS } from '../../types';

interface AddActionMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAddAction: (type: string, method?: string) => void;
}

const AddActionMenu: React.FC<AddActionMenuProps> = ({ anchorEl, onClose, onAddAction }) => {
  const handleAddAction = (type: string, method?: string) => {
    if (type === ACTION_TYPES.CALL && method) {
      onAddAction(type, method);
    } else {
      onAddAction(type);
    }
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.MESSAGE)}>
        <ListItemIcon>
          <MessageIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Message" secondary="Display a message from bot or user" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.TEMPLATE_INPUT)}>
        <ListItemIcon>
          <TemplateIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText
          primary="Template Input"
          secondary="Collect structured input with placeholders"
        />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CHOICE)}>
        <ListItemIcon>
          <ChoiceIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Single Choice" secondary="Present options for single selection" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.MULTIPLE_CHOICE)}>
        <ListItemIcon>
          <MultipleChoiceIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Multiple Choice" secondary="Allow selection of multiple options" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.SUB_PRESET)}>
        <ListItemIcon>
          <SubPresetIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Sub Preset" secondary="Include actions from another preset" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.LOCATION_INPUT)}>
        <ListItemIcon>
          <AddressIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Location Input" secondary="Add location input to the thread" />
      </MenuItem>

      <Divider />

      <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
        Method Calls
      </Typography>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.NAVIGATE)}>
        <ListItemIcon>
          <NavigateIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Navigate" secondary="Go to a different page" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.SET_FORM_FIELDS)}>
        <ListItemIcon>
          <FormFieldsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Set Form Fields" secondary="Update form values" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.SET_CONTEXT)}>
        <ListItemIcon>
          <ContextIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Set Context" secondary="Update conversation context" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.BATCH_GENERATE)}>
        <ListItemIcon>
          <GenerateIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Batch Generate" secondary="AI content generation" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.SEARCH_NONPROFITS)}>
        <ListItemIcon>
          <SearchIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Search Nonprofits" secondary="Find nonprofits by criteria" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.LOAD_NONPROFITS)}>
        <ListItemIcon>
          <NonprofitsIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Load Nonprofits" secondary="Load nonprofit details from IDs" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.LOAD_CAUSES)}>
        <ListItemIcon>
          <AutoAwesome fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Load Causes" secondary="Load SDGs and categories" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.LOAD_PICTURES)}>
        <ListItemIcon>
          <PicturesIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Load Pictures" secondary="Load pictures with resources" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.LOAD_USER)}>
        <ListItemIcon>
          <UserIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Load User" secondary="Load current user data" />
      </MenuItem>

      <MenuItem
        onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.SET_DEFAULT_PICTURE)}
      >
        <ListItemIcon>
          <PicturesIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Set Default Picture" secondary="Set a random picture by category" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.DEBUG)}>
        <ListItemIcon>
          <DebugIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Debug" secondary="Dump context to console" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.WAIT_FOR_CONTEXT)}>
        <ListItemIcon>
          <TimerIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Wait for Context" secondary="Wait for specific context values" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.DELAY)}>
        <ListItemIcon>
          <DelayIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Delay" secondary="Add a timed pause" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.PARSE_DATA)}>
        <ListItemIcon>
          <CodeIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Parse Data" secondary="Transform data using JavaScript" />
      </MenuItem>

      <MenuItem onClick={() => handleAddAction(ACTION_TYPES.CALL, CALL_METHODS.REDUX)}>
        <ListItemIcon>
          <ReduxIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Redux" secondary="Advanced: Direct Redux state access" />
      </MenuItem>
    </Menu>
  );
};

export default AddActionMenu;
